import { Flex, FormControl, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
import { OptionsLayoutAlgorithmValue } from 'highcharts/highcharts';
import { CHART_EDITED, Mixpanel } from 'libs/third-party/Mixpanel';
import {
  ChartSettingsDivider,
  SettingsAccordion,
  Switcher,
} from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetType } from 'types/widget.types';
import { getChartInfo } from 'widgets/ChartWidgets/ChartWidget.helpers';
import { ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import { useWidget } from 'widgets/sdk';

import { DataLabelFont, DataLabelText } from './ChartSettingsLabel';

interface ChartSettingsLabelTreeMapProps {
  widgetType: WidgetType;
}

export const ChartSettingsLabelTreeMap = ({ widgetType }: ChartSettingsLabelTreeMapProps) => {
  const {
    dataLabels: { enabled },
    y2DataLabels,
    updateWidget,
  } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('chart_widget', { useSuspense: false });

  // if the categoryEnabled or valueEnabled is not defined, use the enabled value (can be the case for old widgets)
  const isLevelOneEnabled = enabled;
  const isLevelTwoEnabled = !!y2DataLabels?.enabled;

  const onToggleLevel2Label = () => {
    updateWidget({ y2DataLabels: { enabled: !isLevelTwoEnabled } });
    Mixpanel.track(CHART_EDITED, {
      styling_property: CHART_EDITED.styling_property.Labels,
      styling_value: !isLevelTwoEnabled
        ? CHART_EDITED.styling_value.CategoryLabelsOn
        : CHART_EDITED.styling_value.CategoryLabelsOff,
      chart_type: getChartInfo(widgetType).widgetTypeName,
    });
  };

  const onToggleLevel1Label = () => {
    updateWidget({ dataLabels: { enabled: !isLevelOneEnabled } });
    Mixpanel.track(CHART_EDITED, {
      styling_property: CHART_EDITED.styling_property.Labels,
      styling_value: !enabled ? CHART_EDITED.styling_value.ValueLabelsOn : CHART_EDITED.styling_value.ValueLabelsOff,
      chart_type: getChartInfo(widgetType).widgetTypeName,
    });
  };

  return (
    <SettingsAccordion title={t('labels.label')} isDefaultOpen={true}>
      <Switcher
        label={t('labels.level1Label')}
        onToggle={onToggleLevel1Label}
        isChecked={isLevelOneEnabled}
        style={{ marginTop: 0 }}
      />
      <DataLabelFont />
      <DataLabelText showFontColor />
      <ChartSettingsDivider />

      <Switcher label={t('labels.level2Label')} onToggle={onToggleLevel2Label} isChecked={isLevelTwoEnabled} />
      <DataLabelFont showY2Datalabels />
      <DataLabelText showFontColor showY2Datalabels />
    </SettingsAccordion>
  );
};

export const ChartSettingsLayoutTreeMap = memo(() => {
  const {
    updateWidget,
    generalOptions: { layoutAlgorithm = 'squarified' },
  } = useWidget<ChartWidgetData>();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'layout' });
  const label = t('label');

  const onChange = (layoutAlgorithm: OptionsLayoutAlgorithmValue) =>
    updateWidget({ generalOptions: { layoutAlgorithm } });

  return (
    <>
      <ChartSettingsDivider />
      <FormControl id={'data-format'} as={Flex} align={'center'} marginBottom={1}>
        <Text id={'data-format-layout'} mr='auto' fontSize={'sm'}>
          {label}
        </Text>
        <RadioGroup onChange={onChange} value={layoutAlgorithm} name={label}>
          <Stack direction={'row'} spacing={6}>
            <Radio id={'data-format-squarified'} value={'squarified'} size={'sm'} borderColor={'font.300'}>
              {t('square')}
            </Radio>
            <Radio id={'data-format-stripes'} value={'stripes'} size={'sm'} borderColor={'font.300'}>
              {t('columns')}
            </Radio>
          </Stack>
        </RadioGroup>
      </FormControl>
    </>
  );
});

ChartSettingsLayoutTreeMap.displayName = 'ChartSettingsLayoutTreeMap';
